/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import { type PropsWithChildren } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const styles = {
	table: css({
		width: '100%',
		maxHeight: '100%',
		borderCollapse: 'collapse',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		overflowY: 'auto',
		display: 'block',
		
		'-ms-overflow-style': 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&::-webkit-scrollbar': {
			display: 'none',
		},
	}),
	thead: css({
		position: 'sticky',
		top: 0,
		zIndex: 1,
	}),
	headerRow: css({
		height: '40px',
		backgroundColor: token('elevation.surface.sunken'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
	}),
	thSpace: css({
		verticalAlign: 'middle',
		paddingBlock: 0,
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		// there is a top-level style that has very high-specificty that targets
        // the first cell of any tables and removes padding so we need to override this
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		paddingLeft: `${token('space.100')} !important`,
		color: token('color.text.subtlest'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		textAlign: 'left',
	}),
	thFixed: css({
		verticalAlign: 'middle',
		paddingBlock: 0,
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		color: token('color.text.subtlest'),
		fontWeight: token('font.weight.medium'),
		font: token('font.body'),
		textAlign: 'left',
		borderInlineStartWidth: token('border.width'),
		borderInlineStartStyle: 'solid',
		borderInlineStartColor: token('color.border'),
		whiteSpace: 'nowrap',
	}),
	tfoot: css({
		position: 'sticky',
		bottom: `calc(-1 * ${token('border.width')})`,
		zIndex: 1,
	}),
	footerRow: css({
		height: '40px',
		backgroundColor: token('elevation.surface'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	}),
	footerCell: css({
		verticalAlign: 'middle',
		textAlign: 'right',
		paddingInlineEnd: token('space.150'),
		font: token('font.body'),
		color: token('color.text.subtle'),
	}),
	tdLast: css({
		// there is a top-level style that has very high-specificty that targets
		// the last cell of any tables and removes padding so we need to override this
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		paddingRight: `${token('space.100')} !important`,
	}),
};

const TableHeader = () => {
	const { formatMessage } = useIntl();

	return (
		<thead css={styles.thead}>
			<tr css={styles.headerRow}>
				<th css={styles.thSpace}>{formatMessage(messages.spacesColumnHeader)}</th>
				<th css={styles.thFixed}>{formatMessage(messages.keyColumnHeader)}</th>
				<th css={styles.thFixed}>{formatMessage(messages.typeColumnHeader)}</th>
				<th css={styles.thFixed}>{formatMessage(messages.leadColumnHeader)}</th>
				<th css={styles.thFixed}>{formatMessage(messages.categoryColumnHeader)}</th>
				<th css={[styles.thFixed, styles.tdLast]}>{formatMessage(messages.spaceUrlColumnHeader)}</th>
			</tr>
		</thead>
	);
};

type TableFooterProps = {
	/** Number of results currently shown. */
	shown: number;
	/** Total number of results. Renders as "X+" when total exceeds shown. */
	total: number;
};

const TableFooter = ({ shown, total }: TableFooterProps) => {
	const totalLabel = total > shown ? `${total.toLocaleString()}+` : total.toLocaleString();

	return (
		<tfoot css={styles.tfoot}>
			<tr css={styles.footerRow}>
				<td css={[styles.footerCell, styles.tdLast]} colSpan={6}>
					{shown} of {totalLabel}
				</td>
			</tr>
		</tfoot>
	);
};

type SearchResultsLiteProjectTableProps = PropsWithChildren<{
	/** Number of rows currently rendered. */
	shownCount: number;
	/** Total number of matching results. */
	totalCount: number;
}>;

export const SearchResultsLiteProjectTable = ({
	children,
	shownCount,
	totalCount,
}: SearchResultsLiteProjectTableProps) => (
	<table css={styles.table}>
		<TableHeader />
		<tbody>
			{children}
		</tbody>
		<TableFooter shown={shownCount} total={totalCount} />
	</table>
);
